<template>
  <div class="report-container">
    <div class="report-header">
      <div class="header-content">
        <h2><i class="fas fa-chart-bar"></i> Rapport Produits par Technicien</h2>
        <p>Vue d'ensemble des produits affectés aux techniciens</p>
      </div>
      <div class="header-actions">
        <button @click="loadReport" class="btn-refresh" :disabled="loading">
          <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
          Actualiser
        </button>
        <button @click="exportToExcel" class="btn-export">
          <i class="fas fa-file-excel"></i>
          Exporter Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement du rapport...</p>
    </div>

    <div v-else-if="rapport.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>Aucun technicien avec des produits</p>
    </div>

    <div v-else class="report-content">
      <div v-for="item in rapport" :key="item.technicien._id" class="technicien-card">
        <div class="card-header" @click="toggleDetails(item.technicien._id)">
          <div class="tech-info">
            <div class="tech-avatar">
              <i class="fas fa-user-cog"></i>
            </div>
            <div class="tech-details">
              <h3>{{ item.technicien.prenom }} {{ item.technicien.nom }}</h3>
              <p>{{ item.technicien.email }}</p>
            </div>
          </div>
          
          <div class="tech-stats">
            <div class="stat-item">
              <div class="stat-icon en-sac">
                <i class="fas fa-toolbox"></i>
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ item.produitsEnSac }}</span>
                <span class="stat-label">En sac</span>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon affectes">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ item.produitsAffectes }}</span>
                <span class="stat-label">Affectés</span>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon retournes">
                <i class="fas fa-undo"></i>
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ item.produitsRetournes }}</span>
                <span class="stat-label">Retournés</span>
              </div>
            </div>
          </div>

          <button class="toggle-btn">
            <i class="fas" :class="expandedTech.includes(item.technicien._id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>

        <transition name="slide">
          <div v-if="expandedTech.includes(item.technicien._id)" class="card-details">
            <h4><i class="fas fa-boxes"></i> Produits en sac ({{ item.details.length }})</h4>
            
            <div v-if="item.details.length === 0" class="no-products">
              <i class="fas fa-inbox"></i>
              <p>Aucun produit dans le sac</p>
            </div>

            <div v-else class="products-table">
              <table>
                <thead>
                  <tr>
                    <th>IMEI ou ICCID</th>
                    <th>Désignation</th>
                    <th>Catégorie</th>
                    <th>Quantité</th>
                    <th>Statut</th>
                    <th>Date Affectation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in item.details" :key="product._id">
                    <td><strong>{{ getImeiOrIccid(product) }}</strong></td>
                    <td>{{ product.designation }}</td>
                    <td>
                      <span class="category-badge">{{ product.categorie }}</span>
                    </td>
                    <td>
                      <span class="quantity-badge">{{ product.quantite }}</span>
                    </td>
                    <td>
                      <span class="status-badge" :class="getStatusClass(product.statut)">
                        {{ getStatusLabel(product.statut) }}
                      </span>
                    </td>
                    <td>{{ formatDate(product.dateCreation) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import stockService from '@/services/stockService'
import * as XLSX from 'xlsx'

const store = useStore()
const loading = ref(false)
const rapport = ref([])
const expandedTech = ref([])

const loadReport = async () => {
  loading.value = true
  try {
    const response = await stockService.getTechniciansReport()
    rapport.value = response.data.rapport || []
  } catch (error) {
    console.error('Erreur chargement rapport:', error)
    store.dispatch('notifications/addNotification', {
      type: 'error',
      message: 'Erreur lors du chargement du rapport'
    })
  } finally {
    loading.value = false
  }
}

const toggleDetails = (techId) => {
  const index = expandedTech.value.indexOf(techId)
  if (index > -1) {
    expandedTech.value.splice(index, 1)
  } else {
    expandedTech.value.push(techId)
  }
}

const getStatusClass = (statut) => {
  const classes = {
    'affecte_technicien': 'status-assigned',
    'en_cours': 'status-progress',
    'installe': 'status-installed'
  }
  return classes[statut] || 'status-default'
}

const getStatusLabel = (statut) => {
  const labels = {
    'affecte_technicien': 'Affecté',
    'en_cours': 'En cours',
    'installe': 'Installé'
  }
  return labels[statut] || statut
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('fr-FR')
}

const getImeiOrIccid = (product) => {
  // Afficher IMEI pour GPS, ICCID pour SIM, sinon référence interne
  if (product.categorie === 'GPS' && product.imei) {
    return product.imei
  }
  if (product.categorie === 'SIM' && product.iccid) {
    return product.iccid
  }
  // Fallback: afficher IMEI ou ICCID s'ils existent, sinon référence
  return product.imei || product.iccid || product.referenceInterne || '-'
}

const exportToExcel = () => {
  const data = []
  
  rapport.value.forEach(item => {
    data.push({
      'Technicien': `${item.technicien.prenom} ${item.technicien.nom}`,
      'Email': item.technicien.email,
      'Produits en sac': item.produitsEnSac,
      'Produits affectés': item.produitsAffectes,
      'Produits retournés': item.produitsRetournes
    })
    
    item.details.forEach(product => {
      data.push({
        'Technicien': '',
        'IMEI ou ICCID': getImeiOrIccid(product),
        'Désignation': product.designation,
        'Catégorie': product.categorie,
        'Quantité': product.quantite,
        'Statut': getStatusLabel(product.statut),
        'Date': formatDate(product.dateCreation)
      })
    })
    
    data.push({}) // Ligne vide entre les techniciens
  })

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Rapport Techniciens')
  XLSX.writeFile(wb, `rapport_techniciens_${new Date().toISOString().split('T')[0]}.xlsx`)
  
  store.dispatch('notifications/addNotification', {
    type: 'success',
    message: 'Rapport exporté avec succès'
  })
}

onMounted(() => {
  loadReport()
})
</script>

<style scoped>
.report-container {
  padding: 24px;
  background: #f9fafb;
  min-height: 100vh;
}

.report-header {
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content h2 {
  margin: 0 0 8px 0;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content p {
  margin: 0;
  color: #6b7280;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-refresh,
.btn-export {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn-refresh {
  background: #3b82f6;
  color: white;
}

.btn-refresh:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-export {
  background: #10b981;
  color: white;
}

.btn-export:hover {
  background: #059669;
  transform: translateY(-2px);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 48px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.report-content {
  display: grid;
  gap: 16px;
}

.technicien-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.technicien-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.card-header {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  transition: background 0.3s;
}

.card-header:hover {
  background: #f9fafb;
}

.tech-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.tech-avatar {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.tech-details h3 {
  margin: 0 0 4px 0;
  color: #111827;
  font-size: 18px;
}

.tech-details p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.tech-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.stat-icon.en-sac {
  background: #3b82f6;
}

.stat-icon.affectes {
  background: #10b981;
}

.stat-icon.retournes {
  background: #f59e0b;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
}

.toggle-btn {
  background: #f3f4f6;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.toggle-btn:hover {
  background: #e5e7eb;
}

.card-details {
  padding: 0 20px 20px 20px;
  border-top: 1px solid #e5e7eb;
}

.card-details h4 {
  margin: 20px 0 16px 0;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.no-products {
  text-align: center;
  padding: 40px;
  color: #9ca3af;
}

.no-products i {
  font-size: 32px;
  margin-bottom: 12px;
}

.products-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f9fafb;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  border-bottom: 2px solid #e5e7eb;
}

td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
}

.category-badge,
.quantity-badge,
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.category-badge {
  background: #dbeafe;
  color: #1e40af;
}

.quantity-badge {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.status-assigned {
  background: #dbeafe;
  color: #1e40af;
}

.status-badge.status-progress {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.status-installed {
  background: #d1fae5;
  color: #065f46;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* ========== RESPONSIVE MOBILE ========== */
@media (max-width: 1024px) {
  .report-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .tech-stats {
    gap: 16px;
  }

  .stat-item {
    gap: 8px;
  }

  .stat-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .stat-value {
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  .report-container {
    padding: 12px;
  }

  .report-header {
    padding: 16px;
    margin-bottom: 16px;
  }

  .header-content h2 {
    font-size: 18px;
    gap: 8px;
  }

  .header-content p {
    font-size: 13px;
  }

  .header-actions {
    flex-direction: column;
    gap: 8px;
  }

  .btn-refresh,
  .btn-export {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }

  .card-header {
    padding: 16px;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .tech-info {
    width: 100%;
  }

  .tech-avatar {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .tech-details h3 {
    font-size: 16px;
  }

  .tech-details p {
    font-size: 13px;
  }

  .tech-stats {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }

  .stat-item {
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .stat-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .stat-content {
    align-items: center;
    text-align: center;
  }

  .stat-value {
    font-size: 18px;
  }

  .stat-label {
    font-size: 11px;
  }

  .toggle-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
  }

  .technicien-card {
    position: relative;
  }

  .card-details {
    padding: 0 16px 16px 16px;
  }

  .card-details h4 {
    font-size: 14px;
    margin: 16px 0 12px 0;
  }

  .products-table {
    margin: 0 -16px;
    padding: 0 16px;
  }

  table {
    min-width: 600px;
  }

  th, td {
    padding: 10px 8px;
    font-size: 12px;
  }

  .category-badge,
  .quantity-badge,
  .status-badge {
    padding: 3px 8px;
    font-size: 11px;
  }

  .loading-state,
  .empty-state {
    padding: 40px 16px;
  }

  .spinner {
    width: 40px;
    height: 40px;
  }

  .empty-state i {
    font-size: 40px;
  }
}

@media (max-width: 480px) {
  .report-container {
    padding: 8px;
  }

  .report-header {
    padding: 12px;
    margin-bottom: 12px;
  }

  .header-content h2 {
    font-size: 16px;
  }

  .header-content p {
    font-size: 12px;
  }

  .btn-refresh,
  .btn-export {
    padding: 10px 14px;
    font-size: 13px;
  }

  .card-header {
    padding: 12px;
    gap: 12px;
  }

  .tech-avatar {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .tech-details h3 {
    font-size: 14px;
  }

  .tech-details p {
    font-size: 12px;
  }

  .tech-stats {
    gap: 8px;
  }

  .stat-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .stat-value {
    font-size: 16px;
  }

  .stat-label {
    font-size: 10px;
  }

  .toggle-btn {
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
  }

  .card-details {
    padding: 0 12px 12px 12px;
  }

  .card-details h4 {
    font-size: 13px;
    margin: 12px 0 10px 0;
  }

  .products-table {
    margin: 0 -12px;
    padding: 0 12px;
  }

  th, td {
    padding: 8px 6px;
    font-size: 11px;
  }

  .no-products {
    padding: 24px;
  }

  .no-products i {
    font-size: 24px;
  }
}
</style>
